import React, { useState, useRef, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { Send, Sparkles, X, Zap, MessageSquare, Check, XCircle, RefreshCw, ChevronDown, ChevronUp, ExternalLink, Loader, FileText } from "lucide-react";
import ReactMarkdown from "react-markdown";
import Config from "../../_config/config";
import ApiService from "../../_services/ApiService";
import type { Resource, FlipClassPhaseConfig, Tag } from "./types";
import "./ai-generate-modal.css";

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

export type GenerationType = "content" | "resources" | "quiz" | "quiz-all";

export interface AiGenerateModalContext {
  resourceName?: string;
  resourceType?: string;
  sectionName?: string;
  chapterName?: string;
  moduleName?: string;
  audience?: string;
  difficulty?: string;
  phaseName?: string;
  questionCount?: number;
  bloomsEnabled?: boolean;
}

export interface AiQuizQuestion {
  name: string;
  stemStatement: string;
  explanation: string;
  bloomsLevel?: string;
  choices: Array<{ name: string; choiceString: string; correctFlag: number }>;
}

export interface GenerationOptions {
  bloomsEnabled: boolean;
  courseDocumentMode: "off" | "priority" | "strict";
}

/** @deprecated Use GenerationOptions instead */
export type QuizOptions = GenerationOptions;

type ReviewResource = Resource & {
  status: "approved" | "rejected" | "pending";
  assignedPhase?: string;
};

type ReviewQuestion = AiQuizQuestion & {
  status: "approved" | "rejected";
};

interface AiGenerateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerateNow: (options?: GenerationOptions) => void;
  onGenerateWithInstructions: (conversationContext: ChatMessage[], options?: GenerationOptions) => void;
  generationType: GenerationType;
  contextSummary: AiGenerateModalContext;
  courseDocumentsAvailable?: boolean;
  topicIds?: number[];
  // Resource review props
  generatedResources?: Resource[];
  isGenerating?: boolean;
  generationError?: string | null;
  onConfirmResources?: (resources: Resource[]) => void;
  onRegenerateResources?: (rejected: Array<{ name: string; studyResourceTypeId?: number }>, feedback: string) => void;
  phaseConfigs?: FlipClassPhaseConfig[];
  availableTags?: Tag[];
  // Quiz review props
  generatedQuestions?: AiQuizQuestion[];
  onConfirmQuestions?: (questions: AiQuizQuestion[], tags: Tag[]) => void;
  onRegenerateQuestions?: (rejectedIndices: number[], feedback: string) => void;
}

const CONTENT_CHIPS = [
  { label: "Formal tone", value: "Use a formal academic tone" },
  { label: "Casual tone", value: "Use a casual, conversational tone" },
  { label: "Practical examples", value: "Include practical real-world examples" },
  { label: "Keep it brief", value: "Keep content concise and scannable" },
  { label: "Detailed", value: "Provide comprehensive in-depth coverage" },
  { label: "Include diagrams", value: "Suggest diagrams or visual aids where appropriate" },
];

const RESOURCES_CHIPS = [
  { label: "Videos only", value: "Only generate video resources (YouTube)" },
  { label: "Articles only", value: "Only generate article/text resources" },
  { label: "Mixed types", value: "Mix of articles, videos, and references" },
  { label: "2 resources", value: "Generate exactly 2 resources" },
  { label: "4 resources", value: "Generate exactly 4 resources" },
  { label: "Beginner-friendly", value: "Make resources beginner-friendly with simple explanations" },
];

const QUIZ_CHIPS = [
  { label: "3 questions", value: "Generate exactly 3 questions" },
  { label: "5 questions", value: "Generate exactly 5 questions" },
  { label: "10 questions", value: "Generate exactly 10 questions" },
  { label: "MCQ only", value: "Only multiple choice questions" },
  { label: "Mixed types", value: "Mix of MCQ, true/false, and short answer questions" },
  { label: "True/False", value: "Only true/false questions" },
  { label: "Easy", value: "Keep difficulty level easy" },
  { label: "Hard", value: "Make questions challenging and thought-provoking" },
  { label: "Scenario-based", value: "Use real-world scenarios in questions" },
];

function getChips(type: GenerationType) {
  if (type === "content") return CONTENT_CHIPS;
  if (type === "resources") return RESOURCES_CHIPS;
  return QUIZ_CHIPS;
}

function getTitle(type: GenerationType) {
  switch (type) {
    case "content": return "Generate Content";
    case "resources": return "Generate Resources";
    case "quiz": return "Generate Quiz";
    case "quiz-all": return "Generate Quizzes (All Steps)";
  }
}

function getOpeningMessage(type: GenerationType, ctx: AiGenerateModalContext): string {
  const parts: string[] = [];
  switch (type) {
    case "content":
      parts.push(`I'll generate content for **"${ctx.resourceName || "this resource"}"**`);
      if (ctx.resourceType) parts.push(`(${ctx.resourceType})`);
      parts.push(".");
      if (ctx.sectionName) parts.push(`\n\nSection: *${ctx.sectionName}*`);
      if (ctx.chapterName) parts.push(`| Chapter: *${ctx.chapterName}*`);
      parts.push("\n\nI'll use your module's settings for tone, audience, and difficulty. Want to customize anything before I generate?");
      break;
    case "resources":
      parts.push(`I'll generate study resources for the section **"${ctx.sectionName || "this section"}"**`);
      if (ctx.chapterName) parts.push(`in chapter *${ctx.chapterName}*`);
      parts.push(".");
      parts.push("\n\nBy default I'll create **2-3 mixed resources** (articles, videos, references). Want to customize the types, count, or focus area?");
      break;
    case "quiz": {
      const qCount = ctx.questionCount || 4;
      const bloomsNote = ctx.bloomsEnabled ? ", distributed across Bloom's taxonomy levels" : " at medium difficulty";
      parts.push(`I'll generate a quiz from the content in **"${ctx.sectionName || "this section"}"**`);
      if (ctx.chapterName) parts.push(`(${ctx.chapterName})`);
      parts.push(".");
      parts.push(`\n\nBy default I'll create **${qCount} MCQ questions**${bloomsNote}. Want any changes?`);
      break;
    }
    case "quiz-all": {
      const qCountAll = ctx.questionCount || 4;
      const bloomsNoteAll = ctx.bloomsEnabled ? ", distributed across Bloom's taxonomy levels" : "";
      parts.push(`I'll generate quizzes for **all steps** in the **${ctx.phaseName || "selected phase"}** column.`);
      parts.push(`\n\nEach quiz will have **${qCountAll} MCQ questions**${bloomsNoteAll} by default. Steps that already have a quiz for this phase will be skipped.`);
      parts.push("\n\nWant to customize the quiz settings? Your preferences will apply to all generated quizzes.");
      break;
    }
  }
  return parts.join(" ");
}

function getResourceIcon(typeId?: number, url?: string): string {
  if (url) {
    const lowerUrl = url.toLowerCase();
    if (lowerUrl.includes("youtube.com") || lowerUrl.includes("youtu.be")) return "\u25b6\ufe0f";
    if (lowerUrl.includes("vimeo.com")) return "\ud83c\udfac";
    if (lowerUrl.endsWith(".pdf") || lowerUrl.includes(".pdf?")) return "\ud83d\udcd5";
  }
  const iconMap: Record<number, string> = {
    1: "\ud83d\udce5", 5: "\ud83d\udd17", 6: "\ud83d\udcdd", 13: "\ud83d\udcda",
    16: "\ud83c\udfa5", 31: "\ud83c\udfac", 32: "\u25b6\ufe0f", 37: "\ud83d\udcc4",
  };
  return (typeId && iconMap[typeId]) || "\ud83d\udcc4";
}

function getResourceTypeName(typeId?: number): string {
  const nameMap: Record<number, string> = {
    1: "Download", 5: "Reference", 6: "Article", 13: "E-Book",
    16: "Video", 31: "Vimeo Video", 32: "YouTube Video", 37: "Other",
  };
  return (typeId && nameMap[typeId]) || "Resource";
}

function getYouTubeVideoId(url: string): string | null {
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{11})/);
  return match ? match[1] : null;
}

export default function AiGenerateModal({
  isOpen,
  onClose,
  onGenerateNow,
  onGenerateWithInstructions,
  generationType,
  contextSummary,
  courseDocumentsAvailable,
  topicIds,
  generatedResources,
  isGenerating,
  generationError,
  onConfirmResources,
  onRegenerateResources,
  phaseConfigs,
  availableTags,
  generatedQuestions,
  onConfirmQuestions,
  onRegenerateQuestions,
}: AiGenerateModalProps) {
  const [view, setView] = useState<"choice" | "chat" | "review" | "quiz-review">("choice");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [selectedChips, setSelectedChips] = useState<Set<string>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Bloom's checkbox state
  const [bloomsChecked, setBloomsChecked] = useState(true);

  // Course documents state
  const [courseDocsEnabled, setCourseDocsEnabled] = useState(false);
  const [courseDocMode, setCourseDocMode] = useState<"priority" | "strict">("priority");
  const [courseDocNames, setCourseDocNames] = useState<Array<{ id: number; name: string; typeName: string }>>([]);
  const [courseDocsLoading, setCourseDocsLoading] = useState(false);
  const [courseDocsFetched, setCourseDocsFetched] = useState(false);

  // Resource review state
  const [reviewResources, setReviewResources] = useState<ReviewResource[]>([]);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [replaceFeedback, setReplaceFeedback] = useState("");
  const [showReplaceInput, setShowReplaceInput] = useState(false);

  // Quiz review state
  const [reviewQuestions, setReviewQuestions] = useState<ReviewQuestion[]>([]);
  const [expandedQuestionIndex, setExpandedQuestionIndex] = useState<number | null>(null);
  const [quizReplaceFeedback, setQuizReplaceFeedback] = useState("");
  const [showQuizReplaceInput, setShowQuizReplaceInput] = useState(false);
  const [selectedQuizPhase, setSelectedQuizPhase] = useState<string>("");

  const isQuizType = generationType === "quiz" || generationType === "quiz-all";

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setView("choice");
      setMessages([]);
      setInput("");
      setIsTyping(false);
      setSelectedChips(new Set());
      setBloomsChecked(true);
      setCourseDocsEnabled(false);
      setCourseDocMode("priority");
      setCourseDocNames([]);
      setCourseDocsLoading(false);
      setCourseDocsFetched(false);
      setReviewResources([]);
      setExpandedIndex(null);
      setReplaceFeedback("");
      setShowReplaceInput(false);
      setReviewQuestions([]);
      setExpandedQuestionIndex(null);
      setQuizReplaceFeedback("");
      setShowQuizReplaceInput(false);
    }
  }, [isOpen]);

  // Transition to review view when resources arrive
  useEffect(() => {
    if (generatedResources && generatedResources.length > 0) {
      setReviewResources(
        generatedResources.map(r => ({ ...r, status: "pending" as const }))
      );
      setView("review");
      setShowReplaceInput(false);
      setReplaceFeedback("");
    }
  }, [generatedResources]);

  // Transition to quiz-review view when questions arrive
  useEffect(() => {
    if (generatedQuestions && generatedQuestions.length > 0) {
      setReviewQuestions(
        generatedQuestions.map(q => ({ ...q, status: "approved" as const }))
      );
      setView("quiz-review");
      setShowQuizReplaceInput(false);
      setQuizReplaceFeedback("");
      setExpandedQuestionIndex(null);
    }
  }, [generatedQuestions]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Fetch course document names when toggle is enabled
  useEffect(() => {
    if (courseDocsEnabled && !courseDocsFetched && topicIds && topicIds.length > 0) {
      console.log("[AiGenerateModal] Fetching course documents for topicIds:", topicIds);
      setCourseDocsLoading(true);
      ApiService.listCourseDocuments(topicIds)
        .then(res => {
          console.log("[AiGenerateModal] Course documents response:", res.data);
          setCourseDocNames(res.data.documents || []);
          setCourseDocsFetched(true);
        })
        .catch((err) => {
          console.error("[AiGenerateModal] Failed to fetch course documents:", err);
          setCourseDocNames([]);
          setCourseDocsFetched(true);
        })
        .finally(() => setCourseDocsLoading(false));
    }
  }, [courseDocsEnabled, courseDocsFetched, topicIds]);

  // Focus input when entering chat view
  useEffect(() => {
    if (view === "chat") {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [view]);

  const getGenerationOptions = (): GenerationOptions => {
    return {
      bloomsEnabled: isQuizType && bloomsChecked && !!contextSummary.bloomsEnabled,
      courseDocumentMode: courseDocsEnabled ? courseDocMode : "off",
    };
  };
  // Keep backward-compatible alias
  const getQuizOptions = getGenerationOptions;

  const handleStartChat = () => {
    setView("chat");
    const openingMsg: ChatMessage = {
      id: Date.now().toString(),
      role: "assistant",
      content: getOpeningMessage(generationType, contextSummary),
      timestamp: new Date(),
    };
    setMessages([openingMsg]);
  };

  const toggleChip = (chipValue: string) => {
    setSelectedChips(prev => {
      const next = new Set(prev);
      if (next.has(chipValue)) {
        next.delete(chipValue);
      } else {
        next.add(chipValue);
      }
      return next;
    });
  };

  const sendMessage = async (messageContent?: string) => {
    const content = messageContent || input.trim();
    if (!content && selectedChips.size === 0) return;

    let fullContent = content;
    if (selectedChips.size > 0 && !messageContent) {
      const chipText = Array.from(selectedChips).join(". ");
      fullContent = fullContent ? `${chipText}. ${fullContent}` : chipText;
      setSelectedChips(new Set());
    }

    if (!fullContent) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: "user",
      content: fullContent,
      timestamp: new Date(),
    };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput("");
    setIsTyping(true);

    try {
      const currentUser = localStorage.getItem("currentUser");
      let token: string | null = null;
      if (currentUser && currentUser !== "undefined") {
        try {
          token = JSON.parse(currentUser).token;
        } catch { /* ignore */ }
      }

      if (!token) throw new Error("Please log in to use AI chat");

      const response = await fetch(`${Config.nodeSiteUrl}v1/courses/ai/guided-generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          generationType,
          conversationHistory: updatedMessages
            .filter(m => m.role !== "assistant" || updatedMessages.indexOf(m) > 0)
            .map(m => ({ role: m.role, content: m.content })),
          context: contextSummary,
        }),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      const aiContent = data.response || data.data?.response || data.message || "I'm ready to generate. Click the Generate button when you're satisfied with the preferences.";

      const aiResponse: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: aiContent,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, aiResponse]);
    } catch (error: any) {
      console.error("AI Guided Chat error:", error);
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: `Sorry, I couldn't process that request. ${error.message || "Please try again."}`,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleGenerate = () => {
    if (selectedChips.size > 0) {
      const chipText = Array.from(selectedChips).join(". ");
      const chipMessage: ChatMessage = {
        id: Date.now().toString(),
        role: "user",
        content: chipText,
        timestamp: new Date(),
      };
      onGenerateWithInstructions([...messages, chipMessage], getQuizOptions());
    } else {
      onGenerateWithInstructions(messages, getQuizOptions());
    }
  };

  // ===== Resource review handlers =====

  const toggleResourceStatus = (index: number) => {
    setReviewResources(prev =>
      prev.map((r, i) => {
        if (i !== index) return r;
        return { ...r, status: r.status === "rejected" ? "pending" : "rejected" };
      })
    );
  };

  const setResourcePhase = (index: number, phase: string) => {
    setReviewResources(prev =>
      prev.map((r, i) => (i === index ? { ...r, assignedPhase: phase || undefined } : r))
    );
  };

  const handleConfirm = () => {
    if (!onConfirmResources) return;

    const approved = reviewResources
      .filter(r => r.status !== "rejected")
      .map(r => {
        const resource: Resource = {
          name: r.name,
          resourceType: r.resourceType,
          studyResourceTypeId: r.studyResourceTypeId,
          studyResourceSubTypeId: r.studyResourceSubTypeId,
          content: r.content,
          url: r.url,
          sortOrder: r.sortOrder,
          tags: [],
        };

        if (r.assignedPhase && phaseConfigs && availableTags) {
          const pc = phaseConfigs.find(c => c.phase === r.assignedPhase);
          if (pc) {
            pc.tags.forEach(tagId => {
              const tag = availableTags.find(t => t.id === tagId);
              if (tag) resource.tags!.push(tag);
            });
          }
        }

        return resource;
      });

    onConfirmResources(approved);
  };

  const handleReplace = () => {
    if (!onRegenerateResources) return;
    const rejected = reviewResources
      .filter(r => r.status === "rejected")
      .map(r => ({ name: r.name, studyResourceTypeId: r.studyResourceTypeId }));
    if (rejected.length === 0) return;
    onRegenerateResources(rejected, replaceFeedback);
    setShowReplaceInput(false);
    setReplaceFeedback("");
  };

  // ===== Quiz review handlers =====

  const toggleQuestionStatus = (index: number) => {
    setReviewQuestions(prev =>
      prev.map((q, i) => {
        if (i !== index) return q;
        return { ...q, status: q.status === "rejected" ? "approved" : "rejected" };
      })
    );
  };

  const handleConfirmQuiz = () => {
    if (!onConfirmQuestions) return;
    const approved = reviewQuestions
      .filter(q => q.status !== "rejected")
      .map(({ status, ...q }) => q);
    // Resolve selected phase into Tag objects
    let tags: Tag[] = [];
    if (selectedQuizPhase && phaseConfigs && availableTags) {
      const pc = phaseConfigs.find(c => c.phase === selectedQuizPhase);
      if (pc) {
        tags = pc.tags
          .map(tagId => availableTags.find(t => t.id === tagId))
          .filter((t): t is Tag => !!t);
      }
    }
    onConfirmQuestions(approved, tags);
  };

  const handleReplaceQuestions = () => {
    if (!onRegenerateQuestions) return;
    const rejectedIndices = reviewQuestions
      .map((q, i) => (q.status === "rejected" ? i : -1))
      .filter(i => i !== -1);
    if (rejectedIndices.length === 0) return;
    onRegenerateQuestions(rejectedIndices, quizReplaceFeedback);
    setShowQuizReplaceInput(false);
    setQuizReplaceFeedback("");
  };

  const rejectedCount = reviewResources.filter(r => r.status === "rejected").length;
  const approvedCount = reviewResources.filter(r => r.status !== "rejected").length;

  const rejectedQuestionCount = reviewQuestions.filter(q => q.status === "rejected").length;
  const approvedQuestionCount = reviewQuestions.filter(q => q.status !== "rejected").length;

  const chips = getChips(generationType);

  const modalSize = (view === "review" || view === "quiz-review") ? "xl" as any : view === "chat" ? "lg" : undefined;

  // Bloom's checkbox visible only for quiz types when tenant has it configured
  const showBloomsCheckbox = isQuizType && contextSummary.bloomsEnabled;

  const generatingLabel = isQuizType ? "Generating quiz questions..." : "Generating resources...";

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      centered
      size={modalSize}
      className="ai-generate-modal"
      backdrop="static"
    >
      {/* Loading overlay */}
      {isGenerating && (
        <div className="aigm-generating-overlay">
          <div className="aigm-generating-content">
            <Loader size={32} className="aigm-spinner" />
            <p>{generatingLabel}</p>
            {courseDocsEnabled && courseDocNames.length > 0 && (
              <p className="aigm-generating-docs-hint">
                <FileText size={14} />
                Using {courseDocNames.length} course document{courseDocNames.length !== 1 ? "s" : ""}
                {courseDocMode === "strict" ? " (strict mode)" : " as primary source"}
              </p>
            )}
            <p className="aigm-generating-hint">This may take a few seconds</p>
          </div>
        </div>
      )}

      {view === "choice" ? (
        /* ===== CHOICE VIEW ===== */
        <>
          <div className="aigm-header">
            <div className="aigm-header-content">
              <Sparkles size={20} className="aigm-header-icon" />
              <div>
                <h3>{getTitle(generationType)}</h3>
                {contextSummary.resourceName && (
                  <p className="aigm-subtitle">{contextSummary.resourceName}</p>
                )}
                {contextSummary.sectionName && !contextSummary.resourceName && (
                  <p className="aigm-subtitle">{contextSummary.sectionName}</p>
                )}
                {generationType === "quiz-all" && contextSummary.phaseName && (
                  <p className="aigm-subtitle">{contextSummary.phaseName} - All Steps</p>
                )}
              </div>
            </div>
            <button className="aigm-close" onClick={onClose} aria-label="Close">
              <X size={18} />
            </button>
          </div>

          <div className="aigm-choice-body">
            {generationError && (
              <div className="aigm-error-banner">
                {generationError}
              </div>
            )}
            <p className="aigm-choice-description">
              How would you like to proceed?
            </p>

            {/* Bloom's Taxonomy checkbox */}
            {showBloomsCheckbox && (
              <label className="aigm-blooms-checkbox">
                <input
                  type="checkbox"
                  checked={bloomsChecked}
                  onChange={(e) => setBloomsChecked(e.target.checked)}
                />
                <span className="aigm-blooms-label">
                  <span className="aigm-blooms-title">Bloom's Taxonomy</span>
                  <span className="aigm-blooms-desc">Distribute questions across Bloom's cognitive levels</span>
                </span>
              </label>
            )}

            {/* Course materials toggle */}
            {courseDocumentsAvailable && (
              <div className="aigm-course-docs-toggle">
                <label className="aigm-blooms-checkbox">
                  <input
                    type="checkbox"
                    checked={courseDocsEnabled}
                    onChange={(e) => setCourseDocsEnabled(e.target.checked)}
                  />
                  <span className="aigm-blooms-label">
                    <span className="aigm-blooms-title">Use course materials</span>
                    <span className="aigm-blooms-desc">Ground AI output in documents uploaded to this course</span>
                  </span>
                </label>
                {courseDocsEnabled && (
                  <div className="aigm-course-docs-modes">
                    <label className="aigm-radio-option">
                      <input
                        type="radio"
                        name="courseDocMode"
                        checked={courseDocMode === "priority"}
                        onChange={() => setCourseDocMode("priority")}
                      />
                      <span>
                        <strong>As primary source</strong>
                        <span className="aigm-radio-desc"> — supplements with general knowledge if needed</span>
                      </span>
                    </label>
                    <label className="aigm-radio-option">
                      <input
                        type="radio"
                        name="courseDocMode"
                        checked={courseDocMode === "strict"}
                        onChange={() => setCourseDocMode("strict")}
                      />
                      <span>
                        <strong>Strict</strong>
                        <span className="aigm-radio-desc"> — only from course documents, nothing else</span>
                      </span>
                    </label>

                    {/* Document list */}
                    {courseDocsLoading && (
                      <div className="aigm-course-docs-list">
                        <Loader size={14} className="aigm-spinner" style={{ display: "inline-block", marginRight: 6 }} />
                        <span style={{ fontSize: 13, color: "var(--text-faint)" }}>Loading documents...</span>
                      </div>
                    )}
                    {!courseDocsLoading && courseDocsFetched && courseDocNames.length > 0 && (
                      <div className="aigm-course-docs-list">
                        <span className="aigm-course-docs-count">{courseDocNames.length} document{courseDocNames.length !== 1 ? "s" : ""} available:</span>
                        <ul className="aigm-course-docs-names">
                          {courseDocNames.map(doc => (
                            <li key={doc.id}>{doc.name} <span className="aigm-doc-type">({doc.typeName})</span></li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {!courseDocsLoading && courseDocsFetched && courseDocNames.length === 0 && (
                      <div className="aigm-course-docs-list">
                        <span style={{ fontSize: 13, color: "#b88a00" }}>No documents found for the linked course. AI will use general knowledge.</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <button className="aigm-choice-card aigm-choice-quick" onClick={() => { onGenerateNow(getQuizOptions()); }}>
              <div className="aigm-choice-icon">
                <Zap size={28} />
              </div>
              <div className="aigm-choice-text">
                <h4>Generate Now</h4>
                <p>Use default settings for instant generation. Fast and frictionless.</p>
              </div>
            </button>

            <button className="aigm-choice-card aigm-choice-custom" onClick={handleStartChat}>
              <div className="aigm-choice-icon">
                <MessageSquare size={28} />
              </div>
              <div className="aigm-choice-text">
                <h4>Customize with AI</h4>
                <p>Chat with AI to set your preferences before generating. Fine-tune tone, difficulty, question types, and more.</p>
              </div>
            </button>
          </div>
        </>
      ) : view === "chat" ? (
        /* ===== CHAT VIEW ===== */
        <>
          <div className="aigm-header">
            <div className="aigm-header-content">
              <Sparkles size={20} className="aigm-header-icon" />
              <div>
                <h3>{getTitle(generationType)}</h3>
                <p className="aigm-subtitle">Customize your preferences</p>
              </div>
            </div>
            <button className="aigm-close" onClick={onClose} aria-label="Close">
              <X size={18} />
            </button>
          </div>

          <div className="aigm-chat-body">
            {generationError && (
              <div className="aigm-error-banner" style={{ margin: "12px 16px 0" }}>
                {generationError}
              </div>
            )}
            {/* Course documents banner */}
            {courseDocsEnabled && courseDocNames.length > 0 && (
              <div className="aigm-chat-docs-banner">
                <FileText size={13} />
                <span>
                  Using {courseDocNames.length} course document{courseDocNames.length !== 1 ? "s" : ""}
                  {courseDocMode === "strict" ? " (strict)" : " as primary source"}
                </span>
              </div>
            )}
            {/* Messages */}
            <div className="aigm-messages">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`aigm-message ${message.role === "user" ? "aigm-message-user" : "aigm-message-assistant"}`}
                >
                  {message.role === "assistant" && (
                    <div className="aigm-message-avatar">
                      <Sparkles size={14} />
                    </div>
                  )}
                  <div className="aigm-message-content">
                    {message.role === "assistant" ? (
                      <ReactMarkdown>{message.content}</ReactMarkdown>
                    ) : (
                      message.content
                    )}
                  </div>
                </div>
              ))}

              {isTyping && (
                <div className="aigm-message aigm-message-assistant">
                  <div className="aigm-message-avatar">
                    <Sparkles size={14} />
                  </div>
                  <div className="aigm-typing">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Quick Chips */}
            <div className="aigm-chips-container">
              <div className="aigm-chips">
                {chips.map((chip) => (
                  <button
                    key={chip.value}
                    className={`aigm-chip ${selectedChips.has(chip.value) ? "aigm-chip-selected" : ""}`}
                    onClick={() => toggleChip(chip.value)}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Input + Actions */}
            <div className="aigm-input-area">
              <div className="aigm-input-row">
                <input
                  ref={inputRef}
                  type="text"
                  className="aigm-input"
                  placeholder="Type your preferences..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  disabled={isTyping}
                />
                <button
                  className="aigm-send"
                  onClick={() => sendMessage()}
                  disabled={isTyping || (!input.trim() && selectedChips.size === 0)}
                  aria-label="Send"
                >
                  <Send size={18} />
                </button>
              </div>
              <div className="aigm-actions">
                <button className="aigm-btn-cancel" onClick={onClose}>
                  Cancel
                </button>
                <button
                  className="aigm-btn-generate"
                  onClick={handleGenerate}
                  disabled={isTyping || messages.length < 2}
                >
                  <Sparkles size={16} />
                  Generate Now
                </button>
              </div>
            </div>
          </div>
        </>
      ) : view === "quiz-review" ? (
        /* ===== QUIZ REVIEW VIEW ===== */
        <>
          <div className="aigm-header">
            <div className="aigm-header-content">
              <Sparkles size={20} className="aigm-header-icon" />
              <div>
                <h3>Review Generated Questions</h3>
                <p className="aigm-subtitle">
                  {contextSummary.sectionName || "Section"} &mdash; {approvedQuestionCount} question{approvedQuestionCount !== 1 ? "s" : ""} to add
                  {rejectedQuestionCount > 0 && <span className="aigm-rejected-badge"> ({rejectedQuestionCount} rejected)</span>}
                </p>
                {courseDocsEnabled && courseDocNames.length > 0 && (
                  <p className="aigm-course-docs-badge">
                    <FileText size={13} />
                    Generated from {courseDocNames.length} course document{courseDocNames.length !== 1 ? "s" : ""}
                    {courseDocMode === "strict" && <span className="aigm-strict-tag">strict</span>}
                  </p>
                )}
              </div>
            </div>
            <button className="aigm-close" onClick={onClose} aria-label="Close">
              <X size={18} />
            </button>
          </div>

          <div className="aigm-review-body">
            {generationError && (
              <div className="aigm-error-banner">
                {generationError}
                <button className="aigm-error-retry" onClick={() => onGenerateNow(getQuizOptions())}>
                  Try Again
                </button>
              </div>
            )}

            <div className="aigm-question-list">
              {reviewQuestions.map((question, index) => {
                const isExpanded = expandedQuestionIndex === index;
                const isRejected = question.status === "rejected";
                const correctChoice = question.choices.find(c => c.correctFlag === 1 || (c.correctFlag as any) === true);

                return (
                  <div
                    key={index}
                    className={`aigm-question-card ${isRejected ? "aigm-question-rejected" : ""}`}
                  >
                    {/* Question header */}
                    <div className="aigm-question-header">
                      <div className="aigm-question-info">
                        <span className="aigm-question-number">Q{index + 1}</span>
                        <div className="aigm-question-text-wrap">
                          <span className="aigm-question-stem">{question.stemStatement}</span>
                          {question.bloomsLevel && (
                            <span className="aigm-blooms-tag">{question.bloomsLevel}</span>
                          )}
                        </div>
                      </div>

                      <div className="aigm-question-actions">
                        {/* Expand/collapse */}
                        <button
                          className="aigm-resource-btn aigm-resource-expand"
                          onClick={() => setExpandedQuestionIndex(isExpanded ? null : index)}
                          title={isExpanded ? "Collapse" : "Show details"}
                        >
                          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>

                        {/* Reject/Restore */}
                        <button
                          className={`aigm-resource-btn ${isRejected ? "aigm-resource-restore" : "aigm-resource-reject"}`}
                          onClick={() => toggleQuestionStatus(index)}
                          title={isRejected ? "Restore" : "Reject"}
                        >
                          {isRejected ? <RefreshCw size={16} /> : <XCircle size={16} />}
                        </button>
                      </div>
                    </div>

                    {/* Expandable details */}
                    {isExpanded && !isRejected && (
                      <div className="aigm-question-details">
                        <div className="aigm-question-choices">
                          {question.choices.map((choice, ci) => (
                            <div
                              key={ci}
                              className={`aigm-question-choice ${(choice.correctFlag === 1 || (choice.correctFlag as any) === true) ? "aigm-choice-correct" : ""}`}
                            >
                              <span className="aigm-choice-letter">{choice.choiceString}</span>
                              <span className="aigm-choice-text">{choice.name}</span>
                              {(choice.correctFlag === 1 || (choice.correctFlag as any) === true) && (
                                <Check size={14} className="aigm-choice-check" />
                              )}
                            </div>
                          ))}
                        </div>
                        {question.explanation && (
                          <div className="aigm-question-explanation">
                            <strong>Explanation:</strong> {question.explanation}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Replace rejected section */}
            {rejectedQuestionCount > 0 && (
              <div className="aigm-replace-section">
                {!showQuizReplaceInput ? (
                  <button
                    className="aigm-btn-replace"
                    onClick={() => setShowQuizReplaceInput(true)}
                    disabled={isGenerating}
                  >
                    <RefreshCw size={16} />
                    Replace {rejectedQuestionCount} Rejected Question{rejectedQuestionCount !== 1 ? "s" : ""}
                  </button>
                ) : (
                  <div className="aigm-replace-input-area">
                    <p className="aigm-replace-label">
                      What kind of questions would you like instead? (optional)
                    </p>
                    <div className="aigm-replace-row">
                      <input
                        type="text"
                        className="aigm-input"
                        placeholder="e.g., More application-based, harder questions..."
                        value={quizReplaceFeedback}
                        onChange={(e) => setQuizReplaceFeedback(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleReplaceQuestions();
                          }
                        }}
                      />
                      <button className="aigm-btn-replace-go" onClick={handleReplaceQuestions} disabled={isGenerating}>
                        <RefreshCw size={16} />
                        Replace
                      </button>
                      <button className="aigm-btn-replace-cancel" onClick={() => setShowQuizReplaceInput(false)}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Footer actions */}
            <div className="aigm-confirm-bar">
              <button className="aigm-btn-cancel" onClick={onClose}>
                Cancel
              </button>
              <div className="aigm-confirm-right">
                {phaseConfigs && phaseConfigs.length > 0 && (
                  <select
                    className="aigm-phase-select"
                    value={selectedQuizPhase}
                    onChange={(e) => setSelectedQuizPhase(e.target.value)}
                  >
                    <option value="">No phase tag</option>
                    {phaseConfigs.map(pc => (
                      <option key={pc.phase} value={pc.phase}>{pc.phase.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                    ))}
                  </select>
                )}
                <button
                  className="aigm-btn-generate"
                  onClick={handleConfirmQuiz}
                  disabled={approvedQuestionCount === 0 || isGenerating}
                >
                  <Check size={16} />
                  Confirm &amp; Create Quiz ({approvedQuestionCount})
                </button>
              </div>
            </div>
          </div>
        </>
      ) : (
        /* ===== RESOURCE REVIEW VIEW ===== */
        <>
          <div className="aigm-header">
            <div className="aigm-header-content">
              <Sparkles size={20} className="aigm-header-icon" />
              <div>
                <h3>Review Generated Resources</h3>
                <p className="aigm-subtitle">
                  {contextSummary.sectionName || "Section"} &mdash; {approvedCount} resource{approvedCount !== 1 ? "s" : ""} to add
                  {rejectedCount > 0 && <span className="aigm-rejected-badge"> ({rejectedCount} rejected)</span>}
                </p>
                {courseDocsEnabled && courseDocNames.length > 0 && (
                  <p className="aigm-course-docs-badge">
                    <FileText size={13} />
                    Generated from {courseDocNames.length} course document{courseDocNames.length !== 1 ? "s" : ""}
                    {courseDocMode === "strict" && <span className="aigm-strict-tag">strict</span>}
                  </p>
                )}
              </div>
            </div>
            <button className="aigm-close" onClick={onClose} aria-label="Close">
              <X size={18} />
            </button>
          </div>

          <div className="aigm-review-body">
            {generationError && (
              <div className="aigm-error-banner">
                {generationError}
                <button className="aigm-error-retry" onClick={() => onGenerateNow()}>
                  Try Again
                </button>
              </div>
            )}

            <div className="aigm-resource-list">
              {reviewResources.map((resource, index) => {
                const isExpanded = expandedIndex === index;
                const isRejected = resource.status === "rejected";
                const videoId = resource.url ? getYouTubeVideoId(resource.url) : null;
                const isArticle = resource.studyResourceTypeId === 6;
                const hasPreview = videoId || isArticle || resource.url;

                return (
                  <div
                    key={index}
                    className={`aigm-resource-card ${isRejected ? "aigm-resource-rejected" : ""}`}
                  >
                    {/* Card header */}
                    <div className="aigm-resource-header">
                      <div className="aigm-resource-info">
                        <span className="aigm-resource-icon">
                          {getResourceIcon(resource.studyResourceTypeId, resource.url)}
                        </span>
                        <div className="aigm-resource-details">
                          <span className="aigm-resource-name">{resource.name}</span>
                          <span className="aigm-resource-type">
                            {getResourceTypeName(resource.studyResourceTypeId)}
                          </span>
                        </div>
                      </div>

                      <div className="aigm-resource-actions">
                        {/* Phase dropdown */}
                        {phaseConfigs && phaseConfigs.length > 0 && !isRejected && (
                          <select
                            className="aigm-phase-select"
                            value={resource.assignedPhase || ""}
                            onChange={(e) => setResourcePhase(index, e.target.value)}
                          >
                            <option value="">Untagged</option>
                            {phaseConfigs.map(pc => (
                              <option key={pc.phase} value={pc.phase}>{pc.phase.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                            ))}
                          </select>
                        )}

                        {/* Preview toggle */}
                        {hasPreview && (
                          <button
                            className="aigm-resource-btn aigm-resource-expand"
                            onClick={() => setExpandedIndex(isExpanded ? null : index)}
                            title={isExpanded ? "Collapse" : "Preview"}
                          >
                            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </button>
                        )}

                        {/* Reject/Restore toggle */}
                        <button
                          className={`aigm-resource-btn ${isRejected ? "aigm-resource-restore" : "aigm-resource-reject"}`}
                          onClick={() => toggleResourceStatus(index)}
                          title={isRejected ? "Restore" : "Reject"}
                        >
                          {isRejected ? <RefreshCw size={16} /> : <XCircle size={16} />}
                        </button>
                      </div>
                    </div>

                    {/* Expandable preview */}
                    {isExpanded && !isRejected && (
                      <div className="aigm-resource-preview">
                        {videoId && (
                          <div className="aigm-youtube-embed">
                            <iframe
                              src={`https://www.youtube.com/embed/${videoId}`}
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              title={resource.name}
                            />
                          </div>
                        )}

                        {isArticle && resource.content && (
                          <div className="aigm-article-preview">
                            <ReactMarkdown>{resource.content}</ReactMarkdown>
                          </div>
                        )}

                        {resource.url && !videoId && (
                          <div className="aigm-url-preview">
                            <ExternalLink size={14} />
                            <a href={resource.url} target="_blank" rel="noopener noreferrer">
                              {resource.url}
                            </a>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Replace rejected section */}
            {rejectedCount > 0 && (
              <div className="aigm-replace-section">
                {!showReplaceInput ? (
                  <button
                    className="aigm-btn-replace"
                    onClick={() => setShowReplaceInput(true)}
                    disabled={isGenerating}
                  >
                    <RefreshCw size={16} />
                    Replace {rejectedCount} Rejected Resource{rejectedCount !== 1 ? "s" : ""}
                  </button>
                ) : (
                  <div className="aigm-replace-input-area">
                    <p className="aigm-replace-label">
                      What would you like instead? (optional)
                    </p>
                    <div className="aigm-replace-row">
                      <input
                        type="text"
                        className="aigm-input"
                        placeholder="e.g., More practical examples, different topic..."
                        value={replaceFeedback}
                        onChange={(e) => setReplaceFeedback(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleReplace();
                          }
                        }}
                      />
                      <button className="aigm-btn-replace-go" onClick={handleReplace} disabled={isGenerating}>
                        <RefreshCw size={16} />
                        Replace
                      </button>
                      <button className="aigm-btn-replace-cancel" onClick={() => setShowReplaceInput(false)}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Footer actions */}
            <div className="aigm-confirm-bar">
              <button className="aigm-btn-cancel" onClick={onClose}>
                Cancel
              </button>
              <button
                className="aigm-btn-generate"
                onClick={handleConfirm}
                disabled={approvedCount === 0 || isGenerating}
              >
                <Check size={16} />
                Confirm &amp; Add ({approvedCount})
              </button>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}
